<script setup lang="ts">
import { Quria, GroupV2, DestinyComponentType, DestinyCollectibleState, DestinyCharacterResponse } from 'quria'
import { ref, onMounted, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import { Account, Character, Clan, ErrorToast, TheFooter, formatUsername, useMediaQuery, usePlaywire, triggerRefresh } from '@wastedondestiny/destiny-library'
import SearchBar from '../components/SearchBar.vue'
import ActivityCard from '../components/ActivityCard.vue'
import MainLogo from '../components/MainLogo.vue'
import '@splidejs/vue-splide/css'
import { ActivityType, ExoticActivity } from '../enums/activities'
import { exoticActivities, otherExotics } from '../releaseDetail'
import { Activity, CollectionAccount, LeaderboardPage } from '../types'
import { FinalShapeDungeonHashes, LightfallDungeonHashes, WitchQueenDungeonHashes } from '../enums/dungeons'
import Leaderboard from '../components/Leaderboard.vue'
import { ExoticType } from '../enums/releases'
import ExoticCard from '../components/ExoticCard.vue'

const { getRamp } = usePlaywire()
const route = useRoute()
const router = useRouter()
const isNotMobile = useMediaQuery('(min-width: 640px)')
const clan = ref<GroupV2>()
const page = ref(1)
const clanPages = ref(new Map<string, LeaderboardPage>())
const currentPage = computed(() => {
  const key = `${page.value}`

  return clanPages.value.has(key) ? clanPages.value.get(key) : undefined
})
const allClanMembers = computed<CollectionAccount[]>(() => clanPages.value ? Array.from(clanPages.value.values()).flatMap(x => x.players) : [])
const filter = ref(ActivityType.Raid)
const filterBreakdown = ref<ActivityType|null>(null)
const filterOther = ref<ExoticType>(ExoticType.AutoRifle)
const members = ref([] as CollectionAccount[])
const activities = ref({} as Record<string, Activity[]>)
const loadingActivities = ref([] as string[])
const loadedActivities = ref([] as string[])
const isOpen = ref({
  missing: true,
  acquired: false,
  unavailable: false
})
const isOtherOpen = ref({
  missing: true,
  acquired: false,
  unavailable: false
})
const rotations = ref<Record<string, string>>({})
const loading = ref(false)
const animating = ref(false)
const errors = ref([] as Error[])
const quria = new Quria({
  API_KEY: import.meta.env.VITE_BUNGIE_API_KEY
})
const logo = ref<HTMLElement>()
const now = Date.now()

function activitySort(a: ExoticActivity, b: ExoticActivity) {
  if (a.date < b.date) return 1
  if (a.date > b.date) return -1
  return 0
}

function rotatorSort(a: ExoticActivity, b: ExoticActivity) {
  const ar = rotations.value[a.name]
  const br = rotations.value[b.name]
  if (!(ar && ar.startsWith('Active')) && (br && br.startsWith('Active'))) return 1
  if ((ar && ar.startsWith('Active')) && !(br && br.startsWith('Active'))) return -1
  return 0
}

function guaranteedSort(a: ExoticActivity, b: ExoticActivity) {
  if (a.isGuaranteed && !b.isGuaranteed) return 1
  if (!a.isGuaranteed && b.isGuaranteed) return -1
  return 0
}

function isActive(date: Date) {
  return (now - date.getTime()) < 2_629_743_000 // Milliseconds in a month
}

const allActivities = computed(() => exoticActivities.slice().sort(activitySort).sort(rotatorSort).sort(guaranteedSort))
const filteredActivities = computed(() => filterBreakdown.value !== null ? allActivities.value.filter(x => x.type === filterBreakdown.value) : allActivities.value)
const filteredExotics = computed(() => otherExotics.filter(x => x.weaponType === filterOther.value))
const lastReset = computed(() => {
  const currentDate = new Date(now)
  const closestTuesday = new Date(Date.UTC(currentDate.getUTCFullYear(), currentDate.getUTCMonth(), currentDate.getUTCDate() - currentDate.getUTCDay() + 2, 17))

  if (currentDate.getTime() < closestTuesday.getTime()) {
    closestTuesday.setDate(closestTuesday.getDate() - 7)
  }

  return closestTuesday
})
const nextReset = computed(() => {
  const currentDate = new Date(lastReset.value)
  currentDate.setDate(lastReset.value.getDate() + 7)
  return currentDate  
})

function removeError(index: number) {
  const spliced = errors.value
  spliced.splice(index, 1)
  errors.value = spliced
}

function toggle(tag: 'missing') {
  isOpen.value[tag] = !isOpen.value[tag]
}

function toggleOther(tag: 'missing') {
  isOtherOpen.value[tag] = !isOtherOpen.value[tag]
}

function getDungeonHashes(character: DestinyCharacterResponse) {
  let content: number[] = []
  const dungeonHashes = [...Object.values(WitchQueenDungeonHashes), ...Object.values(LightfallDungeonHashes), ...Object.values(FinalShapeDungeonHashes)]
  const dungeonActivities = character?.activities?.data?.availableActivities?.filter((x: any) => dungeonHashes.includes(x.activityHash) && x.canJoin)

  if (dungeonActivities?.length) content = [...content, ...dungeonActivities.map((x: any) => x.activityHash as number)]

  return content
}

async function addSearchClan(clanResult: Clan) {
  if (clan.value?.groupId === clanResult.id) return

  setLoading(true)

  try {
    await load(clanResult.id)
    router.replace({ name: 'HomePage', params: { clan: clan.value!.groupId } })
  } catch (error: Error|unknown) {
    console.error(error)
    if (error instanceof Error) errors.value = [...errors.value, error]
  }

  setLoading(false)
}

async function* loadCharacterActivities(account: Account, character: Character) {
  let offset = 0

  while (true) {
    const response = await quria.destiny2.GetActivityHistory(character.characterId, account.membershipId, account.membershipType!, {
      count: 250,
      page: offset++
    })

    if (!response.Response?.activities?.length) return
    
    for (const activity of response.Response?.activities) {
      const activityPeriod = new Date(activity.period)

      if (activityPeriod.getTime() < lastReset.value.getTime()) return

      yield {
        id: activity.activityDetails.instanceId,
        modes: activity.activityDetails.modes.length ? activity.activityDetails.modes : [-1],
        completed: activity.values?.['completed']?.basic?.value === 1 && activity.values?.['completionReason']?.basic?.displayValue === 'Objective Completed',
        activityId: activity.activityDetails.referenceId,
        characterId: character.characterId
      }
    }
  }
}

async function loadActivities(accounts: Account[]) {
  for (const account of accounts) {
    if (loadedActivities.value.includes(account.membershipId) || loadingActivities.value.includes(account.membershipId)) continue

    loadingActivities.value.push(account.membershipId)
    activities.value[account.membershipId] = []
    const promises: Promise<void>[] = []

    for (const character of account.characters) {
      promises.push((async () => {
        for await (const activity of loadCharacterActivities(account, character)) {
          activities.value[account.membershipId].push(activity)
        }
      })())
    }
    
    await Promise.allSettled(promises)
    loadedActivities.value.push(account.membershipId)
    loadingActivities.value.splice(loadingActivities.value.indexOf(account.membershipId), 1)
  }
}

async function load(clanId: string) {
  if (clan.value?.groupId === clanId) return

  clan.value = undefined
  members.value = []

  // Load members
  const clanMembers = await quria.groupv2.GetMembersOfGroup(1, clanId, {})
  const promises: Promise<void>[] = []

  if (!clanMembers?.Response) throw new Error('This clan does not seem to exist.', {
    cause: 'notfound'
  })

  for (const member of clanMembers.Response.results) {
    const profilePromise = async () => {
      const profile = await quria.destiny2.GetProfile(member.destinyUserInfo.membershipId, member.destinyUserInfo.membershipType, {
        components: [ DestinyComponentType.Profiles, DestinyComponentType.Collectibles ]
      })
      const allItems = [
        ...Object.entries(profile.Response.profileCollectibles.data.collectibles),
        ...Object.values(profile.Response.characterCollectibles.data).flatMap(x => Object.entries(x.collectibles))
      ]

      const account: CollectionAccount = {
        fireteam: [],
        private: false,
        membershipId: member.destinyUserInfo.membershipId,
        membershipType: member.destinyUserInfo.membershipType,
        characters: profile.Response.profile.data.characterIds.map(x => ({ characterId: x })),
        versionsOwned: profile.Response.profile.data.versionsOwned,
        seasonsOwned: profile.Response.profile.data.seasonHashes,
        dungeonsOwned: [],
        displayName: formatUsername(member.destinyUserInfo.bungieGlobalDisplayName, member.destinyUserInfo.bungieGlobalDisplayNameCode!, member.destinyUserInfo.displayName),
        dateLastPlayed: new Date(+member.lastOnlineStatusChange * 1000), // Seconds to milliseconds
        items: allItems
          .map(([key, val]) => ({
            key: +key,
            has: !(val.state & DestinyCollectibleState.NotAcquired)
          }))
          .sort((a, b) => a.key - b.key)
          .reduce((a, b) => {
            a[b.key] = (a[b.key] || 0) || b.has
            return a
          }, {} as Record<number, boolean>)
      }

      for (const character of account.characters) {
        const characterProfile = await quria.destiny2.GetCharacter(character.characterId, account.membershipId, account.membershipType!, {
          components: [ DestinyComponentType.CharacterActivities ]
        })
        account.dungeonsOwned = [...account.dungeonsOwned!, ...getDungeonHashes(characterProfile.Response)]

        if (!characterProfile.Response?.activities?.data) {
          account.private = true
          
          // Short-circuit for privacy
          throw new Error('This account is private. You can change your privacy settings on <a target="_blank" href="https://www.bungie.net/7/en/User/Account/Privacy">Bungie.net</a>.', {
            cause: 'privacy'
          })
        }
      }
      
      members.value.push(account)
    }

    promises.push(profilePromise())
  }

  await Promise.allSettled(promises)
  loadActivities(members.value)
  
  clanPages.value.clear()

  const sortedMembers = members.value.slice().sort((a, b) => +isActive(b.dateLastPlayed!) - +isActive(a.dateLastPlayed!))
  const clanResult = await quria.groupv2.GetGroup(clanId)

  for (let i = 0; i < sortedMembers.length / 10; i++) {
    const page = i + 1
    const chunk = sortedMembers.slice(i * 10, page * 10)

    clanPages.value.set(`${page}`, {
      count: chunk.length,
      page: page,
      players: chunk,
      total: clanResult.Response.detail.memberCount
    })
  }

  getRamp().then(() => triggerRefresh())
  clan.value = clanResult.Response.detail
}

async function loadFromUrl() {
  if (!route.params.clan) return

  scrollTo({
    top: 0,
    behavior: 'smooth'
  })
  setLoading(true)

  try {
    await load(route.params.clan as string)
    router.replace({ name: 'HomePage', params: { clan: clan.value!.groupId } })
  } catch (error: Error|unknown) {
    console.error(error)
    if (error instanceof Error) errors.value = [...errors.value, error]
  }
  
  setLoading(false)
}

function remove() {
  clan.value = undefined
  members.value = []
  router.replace({ name: 'HomePage', params: { } })
}

async function changePage(pageNumber: number) {
  loading.value = true
  const key = `${pageNumber}`
  const hasLoadedPage = clanPages.value.has(key)
  
  if (page.value === pageNumber && hasLoadedPage) {
    loading.value = false
    return
  }

  page.value = pageNumber
  loading.value = false
}

watch(() => route.params, () => {
  if (route.params.clan as string !== clan.value?.groupId) {
    loadFromUrl()
  }
})

const creatorLink = computed(() => ({
  name: 'HomePage',
  params: {
    clan: '2831399'
  }
}))

onMounted(async () => {
  preventMutations()
  setRotations()
  loadFromUrl()
})

async function setRotations() {
  const milestones = await fetch('https://definition.deepsight.gg/DeepsightDropTableDefinition.json').then(x => x.json())
  rotations.value = Object.values(milestones)
    .map(x => (x as any))
    .filter(x => x.availability && x.type === 'exotic-mission')
    .reduce((a, x) => ({ ...a, [x.displayProperties.name]: (x.availability ? 'Active Weekly ' : 'Weekly ') + x.typeDisplayProperties.name }), {})
}

function preventMutations() {
  const observer = new MutationObserver((mutationList) => {
    for (const mutation of mutationList) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
        if (mutation.target instanceof HTMLElement && mutation.target.getAttribute('style')?.includes('important')) {
          mutation.target.removeAttribute('style')
        }
      }
    }
  })

  observer.observe(logo.value!.getRootNode(), {
    attributes: true,
    subtree: true
  })
}

function setLoading(value: boolean) {
  loading.value = value
  setTimeout(() => {
    animating.value = value
  }, 500)
}
</script>

<template>
  <div class="flex flex-col items-center justify-center h-fit screen">
    <div v-if="!clan" class="flex-grow" />
    <div class="text-center px-1 mt-4 mb-10 text-zinc-200" ref="logo">
      <MainLogo class="max-w-full w-[30rem] sm:w-[40rem] lg:w-[50rem]" />
      <h1 class="text-4xl font-bold shadow-black/50 text-shadow-sm hidden">loot.report</h1>
      <h2 class="text-lg md:text-xl font-thin shadow-black/50 text-shadow-sm hidden">Check what exotic weapons your clanmates are missing on Destiny 2!</h2>
    </div>
    <div class="mb-10 w-full transition-all duration-500 shadow-lg shadow-black/50 rounded-3xl bg-white" :class="{ 'expanded-search': !loading, 'collapsed-search': loading }">
      <div class="relative">
        <div class="py-2 w-[6.5rem] h-12" />
        <div v-if="loading || animating" class="absolute top-0 right-0 w-24 h-10 p-1 m-1 rounded-3xl bg-[#cdad33] text-zinc-100 text-3xl leading-7 font-serif font-bold flex gap-2 justify-center">
          <span class="transition-opacity bullet-1">&bullet;</span>
          <span class="transition-opacity bullet-2">&bullet;</span>
          <span class="transition-opacity bullet-3">&bullet;</span>
        </div>
      </div>
      <SearchBar class="-mt-12" v-if="!loading && !animating" @search="addSearchClan" @error="error => errors = [...errors, error]" />
    </div>
    <!-- <div class="text-zinc-300 flex flex-col items-center mb-8 -mt-4">
      <small>NEW DROP (POD) INCOMING!</small>
      <a href="https://guesstiny.com/" target="_blank" class="block w-full max-w-[320px] rounded-lg overflow-hidden shadow-lg relative after:absolute after:inset-0 hover:after:bg-white/25">
        <img src="../assets/guesstiny.png" alt="GUESSTINY.COM - From Nessus to the Dreaming City, Challenge your knowledge of Destiny" />
      </a>
    </div> -->
    <div class="w-full max-w-[48rem] px-2 py-4 text-zinc-200 mx-auto" v-if="clan">
      <Leaderboard v-if="clan" class="mx-auto max-w-[48rem] h-[46.5rem] sm:h-[30.5rem]" :filter="filter" :loading="loading" :activities="allActivities" :all-clan-members="allClanMembers" :clan="clan" :page="currentPage" @remove-clan="remove" @on-page-change="changePage" @filter="newFilter => filter = newFilter" />
      <h1 id="breakdown" class="text-center text-4xl my-2 border-zinc-500 border-b pb-2">Reward Exotics</h1>
      <div class="flex justify-center items-center gap-1 h-10">
        <span class="leading-4 text-zinc-400 text-sm">Filter activities:</span>
        <select v-model="filterBreakdown" class="sm:hidden bg-zinc-700 p-2">
          <option :value="null">All</option>
          <option :value="ActivityType.Raid">Raids</option>
          <option :value="ActivityType.Dungeon">Dungeons</option>
          <option :value="ActivityType.ExoticMission">Exotic Missions</option>
        </select>
        <button class="w-20 h-10 uppercase text-xs p-1 hidden sm:block" :class="{ 'cursor-default text-black bg-zinc-300': filterBreakdown === null, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterBreakdown !== null }" @click="() => filterBreakdown = null">ALL</button>
        <button class="w-20 h-10 uppercase text-xs p-1 hidden sm:block" :class="{ 'cursor-default text-black bg-zinc-300': filterBreakdown === ActivityType.Raid, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterBreakdown !== ActivityType.Raid }" @click="() => filterBreakdown = ActivityType.Raid">RAIDS</button>
        <button class="w-20 h-10 uppercase text-xs p-1 hidden sm:block" :class="{ 'cursor-default text-black bg-zinc-300': filterBreakdown === ActivityType.Dungeon, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterBreakdown !== ActivityType.Dungeon }" @click="() => filterBreakdown = ActivityType.Dungeon">DUNGEONS</button>
        <button class="w-20 h-10 uppercase text-xs p-1 hidden sm:block" :class="{ 'cursor-default text-black bg-zinc-300': filterBreakdown === ActivityType.ExoticMission, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterBreakdown !== ActivityType.ExoticMission }" @click="() => filterBreakdown = ActivityType.ExoticMission">EXOTIC MISSIONS</button>
      </div>
    </div>
    <div class="flex w-full mb-10 xl:px-48 splide-with-fade" v-if="clan">
      <Splide :options="{ pagination: false, autoWidth: true, focus: 'center', padding: 8 }" class="w-full">
        <SplideSlide v-for="activity in filteredActivities.slice(0, Math.ceil(filteredActivities.length / 2))" :key="activity.id" class="px-2">
          <ActivityCard :activity="activity" :activities="activities" :reset="nextReset" :loading="loadedActivities" :members="members" :rotations="rotations" :is-open="isOpen" @toggle="toggle" />
        </SplideSlide>
        <SplideSlide v-if="isNotMobile && filteredActivities.length % 2 === 0" />
        <SplideSlide v-for="activity in filteredActivities.slice(Math.ceil(filteredActivities.length / 2))" :key="activity.id" class="px-2">
          <ActivityCard :activity="activity" :activities="activities" :reset="nextReset" :loading="loadedActivities" :members="members" :rotations="rotations" :is-open="isOpen" @toggle="toggle" />
        </SplideSlide>
      </Splide>
    </div>
    <div class="w-full max-w-[48rem] px-2 py-4 text-zinc-200 mx-auto" v-if="clan">
      <h1 id="other" class="text-center text-4xl my-2 border-zinc-500 border-b pb-2">Other Exotics</h1>
      <div class="flex justify-center items-center gap-1">
        <span class="leading-4 text-zinc-400 text-sm">Filter weapons:</span>
        <select v-model="filterOther" class="md:hidden bg-zinc-700 p-2">
          <option :value="ExoticType.AutoRifle">Auto Rifle</option>
          <option :value="ExoticType.ScoutRifle">Scout Rifle</option>
          <option :value="ExoticType.PulseRifle">Pulse Rifle</option>
          <option :value="ExoticType.HandCannon">Hand Cannon</option>
          <option :value="ExoticType.Bow">Bow</option>
          <option :value="ExoticType.SubmachineGun">Submachine Gun</option>
          <option :value="ExoticType.Sidearm">Sidearm</option>
          <option :value="ExoticType.TraceRifle">Trace Rifle</option>
          <option :value="ExoticType.FusionRifle">Fusion Rifle</option>
          <option :value="ExoticType.SniperRifle">Sniper Rifle</option>
          <option :value="ExoticType.Shotgun">Shotgun</option>
          <option :value="ExoticType.Glaive">Glaive</option>
          <option :value="ExoticType.GrenadeLauncher">Grenade Launcher</option>
          <option :value="ExoticType.RocketLauncher">Rocket Launcher</option>
          <option :value="ExoticType.Sword">Sword</option>
          <option :value="ExoticType.MachineGun">Machine Gun</option>
        </select>
        <div class="flex-col justify-center items-start gap-y-1 hidden md:flex">
          <div class="flex justify-center items-center gap-1 h-10">
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.AutoRifle, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.AutoRifle }" @click="() => filterOther = ExoticType.AutoRifle">AUTO RIFLE</button>
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.ScoutRifle, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.ScoutRifle }" @click="() => filterOther = ExoticType.ScoutRifle">SCOUT RIFLE</button>
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.PulseRifle, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.PulseRifle }" @click="() => filterOther = ExoticType.PulseRifle">PULSE RIFLE</button>
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.HandCannon, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.HandCannon }" @click="() => filterOther = ExoticType.HandCannon">HAND CANNON</button>
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.Bow, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.Bow }" @click="() => filterOther = ExoticType.Bow">BOW</button>
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.SubmachineGun, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.SubmachineGun }" @click="() => filterOther = ExoticType.SubmachineGun">SUBMACHINE GUN</button>
          </div>
          <div class="flex justify-center items-center gap-1 h-10">
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.Sidearm, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.Sidearm }" @click="() => filterOther = ExoticType.Sidearm">SIDEARM</button>
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.TraceRifle, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.TraceRifle }" @click="() => filterOther = ExoticType.TraceRifle">TRACE RIFLE</button>
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.FusionRifle, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.FusionRifle }" @click="() => filterOther = ExoticType.FusionRifle">FUSION RIFLE</button>
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.SniperRifle, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.SniperRifle }" @click="() => filterOther = ExoticType.SniperRifle">SNIPER RIFLE</button>
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.Shotgun, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.Shotgun }" @click="() => filterOther = ExoticType.Shotgun">SHOTGUN</button>
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.Glaive, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.Glaive }" @click="() => filterOther = ExoticType.Glaive">GLAIVE</button>
          </div>
          <div class="flex justify-center items-center gap-1 h-10">
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.GrenadeLauncher, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.GrenadeLauncher }" @click="() => filterOther = ExoticType.GrenadeLauncher">GRENADE LAUNCHER</button>
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.LinearFusionRifle, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.LinearFusionRifle }" @click="() => filterOther = ExoticType.LinearFusionRifle">LINEAR FUSION RIFLE</button>
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.RocketLauncher, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.RocketLauncher }" @click="() => filterOther = ExoticType.RocketLauncher">ROCKET LAUNCHER</button>
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.Sword, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.Sword }" @click="() => filterOther = ExoticType.Sword">SWORD</button>
            <button class="w-28 h-10 uppercase text-xs p-1" :class="{ 'cursor-default text-black bg-zinc-300': filterOther === ExoticType.MachineGun, 'bg-zinc-800 text-white hover:bg-zinc-600 cursor-pointer': filterOther !== ExoticType.MachineGun }" @click="() => filterOther = ExoticType.MachineGun">MACHINE GUN</button>
          </div>
        </div>
      </div>
    </div>
    <div class="flex w-full mb-10 xl:px-48 splide-with-fade" v-if="clan">
      <Splide :options="{ pagination: false, autoWidth: true, focus: 'center', padding: 8 }" class="w-full">
        <SplideSlide v-for="exotic in filteredExotics.slice(0, Math.ceil(filteredExotics.length / 2))" :key="exotic.name" class="px-2">
          <ExoticCard :exotic="exotic" :members="members" :is-open="isOtherOpen" @toggle="toggleOther" />
        </SplideSlide>
        <SplideSlide v-if="isNotMobile && filteredExotics.length % 2 === 0" />
        <SplideSlide v-for="exotic in filteredExotics.slice(Math.ceil(filteredExotics.length / 2))" :key="exotic.name" class="px-2">
          <ExoticCard :exotic="exotic" :members="members" :is-open="isOtherOpen" @toggle="toggleOther" />
        </SplideSlide>
      </Splide>
    </div>
    <div v-if="!clan" class="flex-grow" />
    <TheFooter name="loot.report" use-playwire>
      <RouterLink class="!decoration-1 !decoration-dotted" :to="creatorLink">&copy; Karmageddon Games {{ (new Date()).getFullYear() }}</RouterLink>
      <template #privacy>
        <RouterLink class="!decoration-1 !decoration-dotted" to="/privacy">Privacy</RouterLink>
      </template>
      <template #ad>
        <!-- <ins class="adsbygoogle w-[320px] h-[100px] sm:w-[468px] sm:h-[60px] lg:w-[728px] lg:h-[90px] block z-10 mx-auto"
          data-ad-client="ca-pub-6332373031553935"
          data-ad-slot="8162200044"
          data-ad-format="auto"
          data-full-width-responsive="true"></ins> -->
        <div id="footer" class="w-full h-[280px] mx-auto flex justify-center"></div>
      </template>
    </TheFooter>
    <div class="fixed bottom-0 flex flex-col-reverse gap-2 mb-4" style="z-index: 9999;">
      <ErrorToast v-for="(error, i) in errors" :key="i" @close="() => removeError(i)">
        <span v-if="error.cause === 'privacy'" v-html="error.message" />
        <span v-else>{{ error?.message || error }}</span>
      </ErrorToast>
    </div>
  </div>
</template>

<style lang="postcss">
html {
  scroll-behavior: smooth;
}

.screen {
  min-height: calc(100dvh - 8rem);
}

.expanded-search {
  max-width: 18rem;
}

.collapsed-search {
  max-width: 6.5rem;
}

@media (min-width: 768px) {
  .expanded-search {
    max-width: 24rem;
  }
}

.bullet-1 {
  opacity: 0;
  animation: dot 1.3s infinite;
  animation-delay: 0.1s;
}

.bullet-2 {
  opacity: 0;
  animation: dot 1.3s infinite;
  animation-delay: 0.2s;
}

.bullet-3 {
  opacity: 0;
  animation: dot 1.3s infinite;
  animation-delay: 0.3s;
}

@keyframes dot {
  0% { opacity: 0; }
 20% { opacity: 1; }
100% { opacity: 0; }
}

.reduced-motion .bullet-1, .reduced-motion .bullet-2, .reduced-motion .bullet-3 {
  animation-play-state: paused;
  opacity: 1;
}

@media (prefers-reduced-motion) {
  .bullet-1, .bullet-2, .bullet-3 {
    animation-play-state: paused;
    opacity: 1;
  }
}

/* Splide fade gradient effect */
.splide-with-fade {
  position: relative;
}

.splide-with-fade .splide__track {
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 16px,
    black calc(100% - 16px),
    transparent 100%
  );
  mask-image: linear-gradient(
    to right,
    transparent 0%,
    black 16px,
    black calc(100% - 16px),
    transparent 100%
  );
}

/* Fallback for older browsers that don't support mask-image */
@supports not (mask-image: linear-gradient(to right, black, black)) {
  .splide-with-fade {
    position: relative;
  }
  
  .splide-with-fade::before,
  .splide-with-fade::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 16px;
    z-index: 10;
    pointer-events: none;
  }
  
  .splide-with-fade::before {
    left: 0;
    background: linear-gradient(to right, var(--bg-color, #1f2937), transparent);
  }
  
  .splide-with-fade::after {
    right: 0;
    background: linear-gradient(to left, var(--bg-color, #1f2937), transparent);
  }
}
</style>
